import { useEffect, useRef, useCallback } from 'react';
import { getCategoryId } from '@/shared/utils/category';

interface UseScrollSpyOptions {
  categories: string[];
  activeCategory?: string;
  onSetActive?: (category: string) => void;
  topOffset?: number;
  bottomOffset?: number;
  debounceMs?: number;
}

export function useScrollSpy({
  categories,
  activeCategory,
  onSetActive,
  topOffset = 250,
  bottomOffset = 200,
  debounceMs = 100,
}: UseScrollSpyOptions) {
  // Ajustar offsets para mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const adjustedTopOffset = isMobile ? 100 : topOffset;
  const adjustedBottomOffset = isMobile ? 100 : bottomOffset;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementRefs = useRef<Map<string, HTMLElement>>(new Map());
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const scrollSpyRef = useRef<HTMLDivElement | null>(null);

  // Função para centralizar a categoria ativa no scroll horizontal
  const scrollToActiveCategory = useCallback((category: string) => {
    if (!scrollSpyRef.current) return;

    const activeButton = scrollSpyRef.current.querySelector(
      `[data-category="${category}"]`
    ) as HTMLElement;
    if (!activeButton) return;

    const container = scrollSpyRef.current;
    const containerRect = container.getBoundingClientRect();
    const buttonRect = activeButton.getBoundingClientRect();

    const scrollLeft = activeButton.offsetLeft - containerRect.width / 2 + buttonRect.width / 2;

    container.scrollTo({
      left: Math.max(0, scrollLeft),
      behavior: 'smooth',
    });
  }, []);

  // Função para fazer scroll suave para uma categoria
  const scrollToCategory = useCallback(
    (category: string) => {
      const id = getCategoryId(category);
      const element = document.getElementById(id);

      if (element) {
        // Encontrar o container de scroll correto
        const scrollContainer = document.querySelector('.overflow-y-auto') as HTMLElement;

        if (scrollContainer) {
          // Scroll dentro do container
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();

          const targetPosition =
            elementRect.top - containerRect.top + scrollContainer.scrollTop - adjustedTopOffset;

          scrollContainer.scrollTo({
            top: Math.max(0, targetPosition),
            behavior: 'smooth',
          });
        } else {
          // Fallback para window scroll
          const elementTop = element.getBoundingClientRect().top + window.scrollY;
          const targetPosition = elementTop - adjustedTopOffset;

          window.scrollTo({
            top: Math.max(0, targetPosition),
            behavior: 'smooth',
          });
        }
      }
    },
    [adjustedTopOffset]
  );

  // Configurar IntersectionObserver para detectar categorias visíveis
  useEffect(() => {
    if (categories.length === 0) return;

    // Limpar observer anterior
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Configurar novo observer
    observerRef.current = new IntersectionObserver(
      entries => {
        // Filtrar apenas entradas que estão intersectando com boa visibilidade
        const visibleEntries = entries
          .filter(entry => entry.isIntersecting && entry.intersectionRatio > 0.1)
          .sort((a, b) => {
            const aRect = a.boundingClientRect;
            const bRect = b.boundingClientRect;
            const viewportHeight = window.innerHeight;

            // Calcular distância do ponto de ativação (adjustedTopOffset do topo OU adjustedBottomOffset do bottom)
            const aDistanceFromTop = Math.abs(aRect.top - adjustedTopOffset);
            const aDistanceFromBottom = Math.abs(
              aRect.bottom - (viewportHeight - adjustedBottomOffset)
            );
            const aDistance = Math.min(aDistanceFromTop, aDistanceFromBottom);

            const bDistanceFromTop = Math.abs(bRect.top - adjustedTopOffset);
            const bDistanceFromBottom = Math.abs(
              bRect.bottom - (viewportHeight - adjustedBottomOffset)
            );
            const bDistance = Math.min(bDistanceFromTop, bDistanceFromBottom);

            return aDistance - bDistance;
          });

        if (visibleEntries.length > 0) {
          const closestEntry = visibleEntries[0];
          const categoryId = closestEntry.target.id;
          const category = categories.find(cat => getCategoryId(cat) === categoryId);

          // Só muda se for realmente diferente e se a categoria for válida
          if (category && category !== activeCategory) {
            // Debounce para evitar mudanças muito rápidas
            if (debounceRef.current) {
              clearTimeout(debounceRef.current);
            }

            debounceRef.current = setTimeout(() => {
              onSetActive?.(category);
            }, debounceMs);
          }
        }
      },
      {
        root: null,
        rootMargin: `-${adjustedTopOffset}px 0px -${adjustedBottomOffset}px 0px`,
        threshold: [0.1, 0.5],
      }
    );

    // Observar elementos após garantir que estejam no DOM
    const observeElements = () => {
      categories.forEach(category => {
        const id = getCategoryId(category);
        const element = document.getElementById(id);

        if (element && observerRef.current) {
          observerRef.current.observe(element);
          elementRefs.current.set(category, element);
        }
      });
    };

    const timer = setTimeout(observeElements, 500);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      clearTimeout(timer);
      elementRefs.current.clear();
    };
  }, [
    categories,
    activeCategory,
    onSetActive,
    adjustedTopOffset,
    adjustedBottomOffset,
    debounceMs,
  ]);

  // Definir primeira categoria como ativa se nenhuma estiver ativa
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      onSetActive?.(categories[0]);
    }
  }, [categories, activeCategory, onSetActive]);

  // Scroll horizontal quando categoria ativa mudar
  useEffect(() => {
    if (activeCategory) {
      scrollToActiveCategory(activeCategory);
    }
  }, [activeCategory, scrollToActiveCategory]);

  return {
    scrollToCategory,
    scrollSpyRef,
  };
}
